import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ExternalLink } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { CondominioDialog, DOCS } from "@/components/CondominioDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Row = ({ c, onEdit, onDelete }) => (
  <tr data-testid={`condominio-row-${c.codice_fiscale}`} className="border-b border-slate-100 transition-colors hover:bg-slate-50/70">
    <td className="px-4 py-3">
      <p className="font-medium text-slate-900">{c.nome}</p>
      <p className="text-xs text-slate-500">{[c.citta, c.provincia].filter(Boolean).join(" · ") || "—"}</p>
    </td>
    <td className="px-4 py-3 font-mono text-sm text-slate-700">{c.codice_fiscale}</td>
    <td className="hidden px-4 py-3 text-sm text-slate-600 md:table-cell">{c.amministratore_nome || "—"}</td>
    <td className="hidden px-4 py-3 lg:table-cell">
      <div className="flex gap-1">
        {DOCS.map(([k, l]) => (
          <span key={k} title={l} className={`h-2 w-6 rounded-full ${c.documenti.includes(k) ? "bg-emerald-500" : "bg-slate-200"}`} />
        ))}
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="flex justify-end gap-1">
        <a href={`/informative?q=${c.codice_fiscale}`} target="_blank" rel="noreferrer" data-testid={`condominio-open-public-${c.codice_fiscale}`} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><ExternalLink className="h-4 w-4" /></a>
        <button onClick={() => onEdit(c)} data-testid={`condominio-edit-${c.codice_fiscale}`} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onDelete(c)} data-testid={`condominio-delete-${c.codice_fiscale}`} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </td>
  </tr>
);

export default function Condomini() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(params.get("nuovo") === "1");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    http.get("/admin/condomini", { params: { q } }).then(({ data }) => setItems(data));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openDialog = (c) => {
    setEditing(c);
    setOpen(true);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && params.get("nuovo")) setParams({});
  };

  const confirmDelete = async () => {
    try {
      await http.delete(`/admin/condomini/${toDelete.id}`);
      toast.success("Condominio eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setToDelete(null);
  };

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Anagrafica</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-slate-900">Condomini</h1>
        </div>
        <button onClick={() => openDialog(null)} data-testid="admin-add-condominio-button" className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-600">
          <Plus className="h-4 w-4" /> Nuovo condominio
        </button>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, C.F., città, amministratore…" data-testid="condomini-search-input" className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-slate-900" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left" data-testid="condomini-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Condominio</th><th className="px-4 py-3">Codice fiscale</th><th className="hidden px-4 py-3 md:table-cell">Amministratore</th><th className="hidden px-4 py-3 lg:table-cell">Documenti</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {items.map((c) => <Row key={c.id} c={c} onEdit={openDialog} onDelete={setToDelete} />)}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="condomini-empty">Nessun condominio trovato.</td></tr>}
          </tbody>
        </table>
      </div>
      <CondominioDialog open={open} onOpenChange={onOpenChange} initial={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il condominio?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.nome} non sarà più consultabile dal portale pubblico.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-button" className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
